import { HeadingPriority } from "atoms/constants/heading-priority";
import Loader from "atoms/loaders/loader";
import Tab from "atoms/tabs/tab";
import TabPanel from "atoms/tabs/tab-panel";
import Heading from "atoms/typography/heading";
import { RecipientType } from "models/enumerations/notebooks/recipient-type";
import NotebookRecord from "models/view-models/notebook-record";
import NotebookPermissionRecipientRecord from "models/view-models/notebooks/notebook-permission-recipient-record";
import { ModalTypes } from "molecules/constants/modal-types";
import NotebookShareMultiSelect from "molecules/form-fields/notebook-share-multi-select";
import BaseModalFooter from "molecules/modals/footers/base-modal-footer";
import BaseModalHeader from "molecules/modals/headers/base-modal-header";
import Modal, { ModalProps } from "molecules/modals/modal";
import SharedPageList from "molecules/shared-page-access-list/shared-page-list";
import TabList from "molecules/tabs/tab-list";
import TabPanels from "molecules/tabs/tab-panels";
import Tabs from "molecules/tabs/tabs";
import useNotebookShareModal from "organisms/modals/notebook-share-modal/use-notebook-share-modal";
import React from "react";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { t } from "utilities/localization-utils";
import NotebookShareModalAccessList from "./notebook-share-modal-access-list";
import NumberBadge from "atoms/badges/number-badge";
import { NotebookShareScope } from "models/enumerations/notebooks/notebook-share-scope";
import useModalActions from "utilities/hooks/use-modal-actions";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
const CSS_CLASS_NAME = "c-notebook-share-modal";
interface NotebookShareModalProps extends Omit<ModalProps, "label" | "type"> {
    notebook: NotebookRecord;
}

const NotebookShareModal = ({
    notebook,
    ...modalProps
}: NotebookShareModalProps) => {
    const { closeDialog } = modalProps;
    const { currentIdentity } = useCurrentIdentity();
    const fullName =
        currentIdentity?.user?.getFirstAndLastNameOrReturnDefault() ?? "";

    const {
        availableRecipients,
        currentlySharedPages,
        currentNotebookShareScope,
        draftRecipientSaves,
        isLoading,
        isLoadingPermissions,
        onAddNewPermission,
        onRemovePermission,
        onSave,
        onUpdateExistingPermission,
        persistedNotebookPermissionsMinusRemovals,
        setDraftSharedPages,
        setDraftScope,
        setRecipientsSearchText,
        isLoadingRecipients,
        isNotebookAccessChanged,
    } = useNotebookShareModal({ closeDialog, notebook });

    const {
        handleClose: handleConfirmationClose,
        handleOpen: handleConfirmationOpen,
        isOpen: isConfirmationOpen,
    } = useModalActions();

    const getParenthecticalValue = (
        recipient: NotebookPermissionRecipientRecord
    ) => {
        if (recipient.type === RecipientType.User) {
            return recipient.email ?? "";
        }

        if (
            recipient.type === RecipientType.Group ||
            recipient.type === RecipientType.WorkGroup
        ) {
            const message = recipient.count
                ? recipient.count
                : "to be shared with all ";
            return `${message} ${t("workgroup-members").toLowerCase()}`;
        }

        return "";
    };

    const pageCount =
        currentNotebookShareScope === NotebookShareScope.EntireNotebook
            ? 0
            : currentlySharedPages.length;
    const contentListRef = React.useRef<HTMLDivElement>(null);
    const disableSaveButton =
        currentlySharedPages.length === 0 &&
        currentNotebookShareScope !== NotebookShareScope.EntireNotebook;

    const handleCancelClick = () => {
        if (isNotebookAccessChanged) {
            handleConfirmationOpen();
        } else {
            closeDialog();
        }
    };
    return (
        <>
            <Modal
                {...modalProps}
                closeDialog={handleCancelClick}
                cssClassName={CSS_CLASS_NAME}
                label={t("shareNotebookName", { notebookName: notebook.name })}
                type={ModalTypes.Base}>
                <BaseModalHeader
                    closeDialog={handleCancelClick}
                    heading={t("shareNotebookName", {
                        notebookName: notebook.name,
                    })}
                />
                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        <section className={`${CSS_CLASS_NAME}__content`}>
                            <Tabs defaultIndex={0}>
                                <TabList>
                                    <Tab
                                        cssClassName={`${CSS_CLASS_NAME}__tab`}>
                                        Who has access
                                    </Tab>
                                    <Tab
                                        cssClassName={`${CSS_CLASS_NAME}__tab`}>
                                        <span
                                            className={`${CSS_CLASS_NAME}__tab__title`}>
                                            Page settings
                                            {pageCount > 0 && (
                                                <NumberBadge
                                                    number={pageCount}
                                                />
                                            )}
                                        </span>
                                    </Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel>
                                        <NotebookShareMultiSelect
                                            onChange={onAddNewPermission}
                                            currentValues={draftRecipientSaves}
                                            persistedValues={
                                                persistedNotebookPermissionsMinusRemovals
                                            }
                                            availableRecipients={
                                                availableRecipients
                                            }
                                            setRecipientsSearchText={
                                                setRecipientsSearchText
                                            }
                                            isLoadingRecipients={
                                                isLoadingRecipients
                                            }
                                        />
                                        <div
                                            className={`${CSS_CLASS_NAME}__content__lists`}
                                            ref={contentListRef}>
                                            <NotebookShareModalAccessList
                                                accessListType={"individual"}
                                                isLoadingPermissions={
                                                    isLoadingPermissions
                                                }
                                                fullName={fullName}
                                                draftRecipientSaves={
                                                    draftRecipientSaves
                                                }
                                                persistedNotebookPermissions={
                                                    persistedNotebookPermissionsMinusRemovals
                                                }
                                                getParentheticalValue={
                                                    getParenthecticalValue
                                                }
                                                onUpdateExistingPermission={
                                                    onUpdateExistingPermission
                                                }
                                                onRemovePermission={
                                                    onRemovePermission
                                                }
                                                scrollableElementRef={
                                                    contentListRef
                                                }
                                            />
                                            <NotebookShareModalAccessList
                                                accessListType={"group"}
                                                isLoadingPermissions={
                                                    isLoadingPermissions
                                                }
                                                fullName={fullName}
                                                draftRecipientSaves={
                                                    draftRecipientSaves
                                                }
                                                persistedNotebookPermissions={
                                                    persistedNotebookPermissionsMinusRemovals
                                                }
                                                getParentheticalValue={
                                                    getParenthecticalValue
                                                }
                                                onUpdateExistingPermission={
                                                    onUpdateExistingPermission
                                                }
                                                onRemovePermission={
                                                    onRemovePermission
                                                }
                                                scrollableElementRef={
                                                    contentListRef
                                                }
                                            />
                                        </div>
                                    </TabPanel>
                                    <TabPanel>
                                        <div
                                            className={`${CSS_CLASS_NAME}__shared-pages`}>
                                            <Heading
                                                cssClassName={`${CSS_CLASS_NAME}__shared-list-heading`}
                                                priority={
                                                    HeadingPriority.Three
                                                }>
                                                {t("pageAccess")}
                                            </Heading>
                                            <SharedPageList
                                                allPagesInNotebook={
                                                    notebook.pages ?? []
                                                }
                                                currentSharedPages={
                                                    currentlySharedPages
                                                }
                                                currentScope={
                                                    currentNotebookShareScope
                                                }
                                                onScopeChange={(scope) =>
                                                    setDraftScope(scope)
                                                }
                                                onPageShareChange={(
                                                    newPages
                                                ) => {
                                                    setDraftSharedPages([
                                                        ...newPages,
                                                    ]);
                                                }}
                                            />
                                        </div>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </section>
                        <BaseModalFooter
                            primaryButtonProps={{
                                onClick: onSave,
                                disabled: disableSaveButton,
                            }}
                            closeButtonProps={{ onClick: handleCancelClick }}
                            closeButtonText={t("cancel")}
                            primaryText={t("save")}
                            closeDialog={handleCancelClick}
                        />
                    </>
                )}
            </Modal>
            <ConfirmationModal
                isVisible={isConfirmationOpen}
                message={t("confirmation-modal-notebook-sharing--body-text")}
                onCancel={handleConfirmationClose}
                onConfirm={closeDialog}
            />
        </>
    );
};

export default NotebookShareModal;
